import { Injectable, signal } from '@angular/core';
import { environment } from '../../environments/environment';

interface TokenData {
  access_token: string;
  expires_in?: number;
  token_type?: string;
  [key: string]: unknown;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly STORAGE_KEY_TOKENS = 'delfiTokens';
  private readonly STORAGE_KEY_SAVED_AT = 'delfiTokenSavedAt';

  /** Reactive signal — components read this to react when auth state changes. */
  readonly authenticated = signal(this.isAuthenticated());

  private get redirectUri(): string {
    return environment.redirectUri;
  }

  async startAuthFlow(): Promise<void> {
    const codeVerifier = this.generateCodeVerifier();
    const codeChallenge = await this.generateCodeChallenge(codeVerifier);

    // Store verifier for later exchange
    sessionStorage.setItem('pkce_code_verifier', codeVerifier);

    const authUrl = this.buildAuthorizeUrl(codeChallenge);
    window.location.href = authUrl;
  }

  async handleCallback(code: string): Promise<TokenData> {
    const codeVerifier = sessionStorage.getItem('pkce_code_verifier');
    if (!codeVerifier) {
      throw new Error('No PKCE code verifier found. Please restart the sign-in flow.');
    }

    sessionStorage.removeItem('pkce_code_verifier');

    const tokenResponse = await this.exchangeCodeForToken(code, codeVerifier);
    this.saveTokens(tokenResponse);
    this.authenticated.set(true);
    return tokenResponse;
  }

  getAccessToken(): string | null {
    const tokensRaw = localStorage.getItem(this.STORAGE_KEY_TOKENS);
    const savedAtRaw = localStorage.getItem(this.STORAGE_KEY_SAVED_AT);

    if (!tokensRaw) return null;

    const tokens: TokenData = JSON.parse(tokensRaw);
    const token = tokens.access_token;
    if (!token) return null;

    const savedAt = savedAtRaw ? Number(savedAtRaw) : 0;
    const expired =
      this.isExpiredByExpClaim(token) ||
      this.isExpiredByExpiresIn(tokens, savedAt);

    if (expired) {
      this.clearTokens();
      return null;
    }

    return token;
  }

  isAuthenticated(): boolean {
    return this.getAccessToken() !== null;
  }

  signOut(): void {
    this.clearTokens();
    this.authenticated.set(false);
  }

  // --- Private helpers ---

  private generateCodeVerifier(length = 64): string {
    const randomBytes = new Uint8Array(length);
    crypto.getRandomValues(randomBytes);
    return this.base64urlFromBytes(randomBytes);
  }

  private async generateCodeChallenge(codeVerifier: string): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(codeVerifier);
    const digest = await crypto.subtle.digest('SHA-256', data);
    return this.base64urlFromBytes(new Uint8Array(digest));
  }

  private buildAuthorizeUrl(codeChallenge: string): string {
    const url = new URL(environment.authorizationEndpoint);
    url.searchParams.set('response_type', 'code');
    url.searchParams.set('client_id', environment.clientId);
    url.searchParams.set('redirect_uri', this.redirectUri);
    url.searchParams.set('code_challenge', codeChallenge);
    url.searchParams.set('code_challenge_method', 'S256');
    url.searchParams.set('scope', environment.scope);
    url.searchParams.set('prompt', 'login');
    url.searchParams.set('state', this.generateState());
    return url.toString();
  }

  private async exchangeCodeForToken(code: string, codeVerifier: string): Promise<TokenData> {
    const params: Record<string, string> = {
      grant_type: 'authorization_code',
      client_id: environment.clientId,
      code,
      redirect_uri: this.redirectUri,
      code_verifier: codeVerifier,
      appKey: environment.appKey
    };

    // Confidential clients (Web server application profile) require client_secret.
    // Public clients (Browser based SPA profile) use PKCE only — leave clientSecret empty.
    if (environment.clientSecret) {
      params['client_secret'] = environment.clientSecret;
    }

    const body = new URLSearchParams(params);

    const response = await fetch(environment.tokenEndpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body
    });

    if (!response.ok) {
      const text = await response.text();
      throw new Error(`Token exchange failed: ${response.status} ${response.statusText} - ${text}`);
    }

    return response.json();
  }

  private saveTokens(tokens: TokenData): void {
    localStorage.setItem(this.STORAGE_KEY_TOKENS, JSON.stringify(tokens));
    localStorage.setItem(this.STORAGE_KEY_SAVED_AT, String(Date.now()));
  }

  private clearTokens(): void {
    localStorage.removeItem(this.STORAGE_KEY_TOKENS);
    localStorage.removeItem(this.STORAGE_KEY_SAVED_AT);
  }

  private generateState(): string {
    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);
    return this.base64urlFromBytes(bytes);
  }

  private base64urlFromBytes(bytes: Uint8Array): string {
    let binary = '';
    for (let i = 0; i < bytes.length; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    const base64 = btoa(binary);
    return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
  }

  private isExpiredByExpClaim(accessToken: string, skewSeconds = 60): boolean {
    const exp = this.parseJwtExp(accessToken);
    if (!exp) return false;
    const now = Math.floor(Date.now() / 1000);
    return now >= (exp - skewSeconds);
  }

  private isExpiredByExpiresIn(tokens: TokenData, savedAtMs: number, skewSeconds = 60): boolean {
    const expiresIn = Number(tokens.expires_in);
    if (!Number.isFinite(expiresIn) || !Number.isFinite(savedAtMs)) return false;
    const expiresAtMs = savedAtMs + expiresIn * 1000;
    return Date.now() >= (expiresAtMs - skewSeconds * 1000);
  }

  private parseJwtExp(accessToken: string): number | null {
    try {
      const [, payload] = accessToken.split('.');
      if (!payload) return null;
      const pad = '='.repeat((4 - (payload.length % 4)) % 4);
      const s = (payload + pad).replace(/-/g, '+').replace(/_/g, '/');
      const json = JSON.parse(atob(s));
      return typeof json.exp === 'number' ? json.exp : null;
    } catch {
      return null;
    }
  }
}
